import { memo, useMemo } from 'react'
import { Avatar, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Scorer, ScorerTeamResult } from 'types/roster'

type Props = {
    data: Scorer[]
    loading: boolean
}

const teamResultColumns: ColumnsType<ScorerTeamResult> = [
    { title: 'Team', dataIndex: 'team', key: 'team' },
    { title: 'Pos', dataIndex: 'positionShortName', key: 'positionShortName' },
    { title: 'P', dataIndex: 'played', key: 'played' },
    { title: 'G', dataIndex: 'count_goals', key: 'count_goals' },
    { title: 'GP', dataIndex: 'count_goal_passes', key: 'count_goal_passes' },
    { title: 'G+GP', dataIndex: 'count_goals_passes_sum', key: 'count_goals_passes_sum' },
    { title: 'YC', dataIndex: 'yellow_card', key: 'yellow_card' },
    { title: 'RC', dataIndex: 'red_card', key: 'red_card' },
    { title: 'CS', dataIndex: 'clean_sheets', key: 'clean_sheets' },
]

export const LeagueScorerTable = memo((props: Props) => {
    const { data, loading } = props

    const columns: ColumnsType<Scorer> = useMemo(
        () => [
            {
                title: 'Place',
                dataIndex: 'result',
                key: 'result',
                width: 60,
            },
            {
                title: 'Player',
                key: 'player',
                render: (_, record) => (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {record.photo && <Avatar src={record.photo} size='small' />}
                        {record.playerName} {record.playerSurname}
                    </span>
                ),
            },
            {
                title: 'Team',
                key: 'team',
                render: (_, record) =>
                    record.teamsResults?.length === 1
                        ? record.teamsResults[0].team
                        : `${record.teamsResults?.length ?? 0} teams`,
            },
            {
                title: 'Pos',
                key: 'position',
                render: (_, record) =>
                    record.teamsResults?.length === 1 ? record.teamsResults[0].positionShortName ?? '—' : '—',
            },
            {
                title: 'P',
                dataIndex: 'played',
                key: 'played',
                sorter: (a, b) => a.played - b.played,
            },
            {
                title: 'G',
                dataIndex: 'count_goals',
                key: 'count_goals',
                sorter: (a, b) => a.count_goals - b.count_goals,
            },
            {
                title: 'GP',
                dataIndex: 'count_goal_passes',
                key: 'count_goal_passes',
                sorter: (a, b) => a.count_goal_passes - b.count_goal_passes,
            },
            {
                title: 'G+GP',
                dataIndex: 'count_goals_passes_sum',
                key: 'count_goals_passes_sum',
                sorter: (a, b) => a.count_goals_passes_sum - b.count_goals_passes_sum,
            },
            {
                title: 'YC',
                dataIndex: 'yellow_card',
                key: 'yellow_card',
                sorter: (a, b) => a.yellow_card - b.yellow_card,
            },
            {
                title: 'RC',
                dataIndex: 'red_card',
                key: 'red_card',
                sorter: (a, b) => a.red_card - b.red_card,
            },
            {
                title: 'CS',
                dataIndex: 'clean_sheets',
                key: 'clean_sheets',
                sorter: (a, b) => a.clean_sheets - b.clean_sheets,
            },
            {
                title: 'Rating',
                key: 'rating',
                render: (_, record) => {
                    const { rating, oldRating, wins, draws, losses } = record.ratings ?? {}
                    return (
                        <Tooltip title={`Old: ${oldRating ?? '—'} | W:${wins ?? 0} D:${draws ?? 0} L:${losses ?? 0}`}>
                            {rating ?? '—'}
                        </Tooltip>
                    )
                },
            },
        ],
        []
    )

    return (
        <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey='playerId'
            pagination={false}
            expandable={{
                expandedRowRender: record =>
                    record.teamsResults?.length > 1 ? (
                        <Table
                            columns={teamResultColumns}
                            dataSource={record.teamsResults}
                            pagination={false}
                            rowKey='teamId'
                            size='small'
                        />
                    ) : null,
                rowExpandable: record => (record.teamsResults?.length ?? 0) > 1,
            }}
        />
    )
})
